import React, { useState, useEffect } from 'react';
import ContentEditable from 'react-contenteditable';
import { stripAllFormatting } from '../../utils/textUtils';

interface TextOptionVariations {
  original: string;
  tuSingular: string;
  tuPlural: string;
  udSingular: string;
  udPlural: string;
}

interface TextSelectableArchitectContent {
  textOptions: TextOptionVariations[];
  selectedOptionIndex?: number; // Index of the selected option for the client
}

interface TextSelectableArchitectEditorProps {
  content: string[] | TextSelectableArchitectContent;
  onChange: (content: TextSelectableArchitectContent) => void;
  isEditable: boolean;
  onEditableChange: (editable: boolean) => void;
  showEditableToggle?: boolean;
  projectClientSize?: 'SINGULAR' | 'PLURAL' | string;
  projectClientTone?: 'INFORMAL' | 'FORMAL' | string;
}

type TabKey = keyof TextOptionVariations;

const TextSelectableArchitectEditor: React.FC<TextSelectableArchitectEditorProps> = ({
  content,
  onChange,
  isEditable,
  onEditableChange,
  showEditableToggle = true,
  projectClientSize,
  projectClientTone
}) => {
  // Determine which variation to show based on project configuration
  const getProjectVariation = (): TabKey => {
    if (!projectClientTone || !projectClientSize) {
      return 'original'; // Fallback to original if configuration is missing
    }

    if (projectClientTone === 'INFORMAL' && projectClientSize === 'SINGULAR') {
      return 'tuSingular';
    } else if (projectClientTone === 'INFORMAL' && projectClientSize === 'PLURAL') {
      return 'tuPlural';
    } else if (projectClientTone === 'FORMAL' && projectClientSize === 'SINGULAR') {
      return 'udSingular';
    } else if (projectClientTone === 'FORMAL' && projectClientSize === 'PLURAL') {
      return 'udPlural';
    }

    return 'original'; // Default fallback
  };

  const activeTab = getProjectVariation();
  const [isEditing, setIsEditing] = useState(false);

  // Initialize state from content
  const [textOptions, setTextOptions] = useState<TextOptionVariations[]>(() => {
    if (Array.isArray(content)) {
      // Legacy format - convert strings to variation objects
      return content.map(text => ({
        original: text,
        tuSingular: '',
        tuPlural: '',
        udSingular: '',
        udPlural: ''
      }));
    } else if (content && typeof content === 'object' && content.textOptions) {
      return content.textOptions;
    }
    return [];
  });

  const [selectedOptionIndex, setSelectedOptionIndex] = useState<number>(() => {
    if (content && typeof content === 'object' && 'textOptions' in content && content.selectedOptionIndex !== undefined) {
      return content.selectedOptionIndex;
    }
    return 0; // Default to first option
  });

  // Get current variation label
  const getVariationLabel = (tabKey: TabKey): string => {
    const tabLabels = {
      'original': 'Original',
      'tuSingular': 'TÚ SINGULAR',
      'tuPlural': 'TÚ PLURAL',
      'udSingular': 'UD. SINGULAR',
      'udPlural': 'UD. PLURAL'
    };
    return tabLabels[tabKey];
  };

  // Update parent when data changes
  useEffect(() => {
    onChange({
      textOptions,
      selectedOptionIndex
    });
  }, [textOptions, selectedOptionIndex, onChange]);

  // Handle option selection
  const handleOptionSelect = (index: number) => {
    setSelectedOptionIndex(index);
  };

  // Handle text editing
  const handleTextChange = (value: string) => {
    if (selectedOptionIndex < textOptions.length) {
      const updatedOptions = [...textOptions];
      updatedOptions[selectedOptionIndex] = {
        ...updatedOptions[selectedOptionIndex],
        [activeTab]: value
      };
      setTextOptions(updatedOptions);
    }
  };

  // Apply formatting
  const applyFormatting = (command: string, value?: string) => {
    document.execCommand(command, false, value);
  };

  // Clean formatting

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  if (textOptions.length === 0) {
    return (
      <div className="mt-4 pt-2">
        <div className="text-center text-muted py-4">
          <i className="bi bi-inbox fs-1 d-block mb-2"></i>
          <div>No hay opciones de texto disponibles</div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 pt-2">
      <label className="form-label">Selección y Edición de Texto</label>
      <small className="text-muted d-block mb-3">
        Selecciona la opción de texto que se mostrará al cliente y edítala según sea necesario.
      </small>

      {/* Option Selection */}
      <div className="mb-4">
        <label className="form-label">Opciones disponibles:</label>
        <div className="border rounded p-3">
          {textOptions.map((option, index) => (
            <div key={index} className="form-check mb-4">
              <input
                className="form-check-input"
                type="radio"
                name="textOption"
                id={`option-${index}`}
                checked={selectedOptionIndex === index}
                onChange={() => handleOptionSelect(index)}
              />
              <label className="form-check-label" htmlFor={`option-${index}`}>
                <strong>Opción {index + 1}:</strong> {option.original || 'Texto vacío'}
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Selected Option Editor */}
      {selectedOptionIndex < textOptions.length && (
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <label className="form-label mb-0">
              Editando Opción {selectedOptionIndex + 1}
            </label>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setIsEditing(!isEditing)}
            >
              {isEditing ? 'Vista previa' : 'Editar'}
            </button>
          </div>

          {isEditing ? (
            <>
              {/* Editor */}
              <div
                className="border rounded"
              >
                {/* Formatting Toolbar */}
                <div className="d-flex align-items-center border-bottom bg-light">
                  <div className="btn-group h-100" role="group" aria-label="Formato de texto">
                    <button
                      type="button"
                      className="btn btn-outline-secondary h-100 border-0"
                      onClick={() => applyFormatting('bold')}
                      title="Negrita"
                      style={{ borderRadius: 0 }}
                    >
                      <i className="bi bi-type-bold"></i>
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary h-100 border-0"
                      onClick={() => applyFormatting('italic')}
                      title="Cursiva"
                      style={{ borderRadius: 0 }}
                    >
                      <i className="bi bi-type-italic"></i>
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary h-100 border-0"
                      onClick={() => applyFormatting('underline')}
                      title="Subrayado"
                      style={{ borderRadius: 0 }}
                    >
                      <i className="bi bi-type-underline"></i>
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary h-100 border-0"
                      onClick={() => {
                        // Try to clean selected text first, if no selection clean all
                        const selection = window.getSelection();
                        if (selection && selection.rangeCount > 0 && !selection.isCollapsed) {
                          // Clean format of selected text only
                          document.execCommand('removeFormat', false);
                        } else {
                          // Clean all formatting
                          const currentText = textOptions[selectedOptionIndex][activeTab];
                          handleTextChange(stripAllFormatting(currentText));
                        }
                      }}
                      title="Limpiar formato (selección o todo)"
                      style={{ borderRadius: 0 }}
                    >
                      <i className="bi bi-eraser"></i>
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary h-100 border-0"
                      onClick={() => applyFormatting('insertUnorderedList')}
                      title="Lista desordenada"
                      style={{ borderRadius: 0 }}
                    >
                      <i className="bi bi-list-ul"></i>
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary h-100 border-0"
                      onClick={() => applyFormatting('insertOrderedList')}
                      title="Lista ordenada"
                      style={{ borderRadius: 0 }}
                    >
                      <i className="bi bi-list-ol"></i>
                    </button>
                  </div>
                </div>

                {/* Content Editor */}
                <ContentEditable
                  html={textOptions[selectedOptionIndex][activeTab]}
                  disabled={false}
                  onChange={(evt) => handleTextChange(evt.target.value)}
                  onPaste={handlePaste}
                  style={{
                    padding: '1rem',
                    minHeight: '200px',
                    backgroundColor: '#fff',
                    border: 'none',
                    outline: 'none'
                  }}
                  className="w-100"
                  placeholder="Escribe el contenido..."
                />
              </div>
            </>
          ) : (
            // Preview mode
            <div className="border rounded p-3 bg-light">
              <div
                dangerouslySetInnerHTML={{
                  __html: textOptions[selectedOptionIndex][activeTab] || 'Texto vacío'
                }}
              />
            </div>
          )}

        </div>
      )}

      {/* Editable Toggle */}
      {showEditableToggle && (
        <div className="mt-4">
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="textSelectableEditable"
              checked={isEditable}
              onChange={(e) => onEditableChange(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="textSelectableEditable" style={{ color: '#000' }}>
              Permitir edición por Arquitectos
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

export default TextSelectableArchitectEditor;